import { PocRadio } from 'commons/components/components-wrapper/poc-radio.component';
import { PocRadioGroup } from 'commons/components/components-wrapper/poc-radio-group.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import type {
    PocDatepickerValueType,
    PocInputError,
    PocInputType,
    PocInputValidity,
    PocInputWarning,
} from 'commons/types';
import { DatePicker, ExtendedValidationMessages, Input, Select, SelectOption } from 'commons/components';
import { useTranslation, TFunction } from 'core/i18n/i18n';
import { CustomValidationCode } from 'core/utils/backend-service.ts';
import { isArray } from 'lodash';
import React, { ReactElement, useCallback } from 'react';

import { DeviceConfigurationValidity } from 'modules/settings/contexts/device-configuration/device-configuration.type';
import { useConfigurationValidation } from 'modules/settings/contexts/device-configuration/hooks/use-device-configuration-validation.hook.ts';
import { useDeviceConfiguration } from 'modules/settings/contexts/device-configuration/hooks/use-device-configuration.hook.ts';
import { CheckboxGroupComponent } from './components/checkbox-group.component';
import { PROPERTY_STATUS } from './constants.ts';
import {
    DeviceLayoutConfigPropertyContentType,
    DeviceLayoutConfigRepresentationType,
    DeviceLayoutConfigSectionProperty,
    MaxLengthValidator,
    MaxSelectableValuesValidator,
    MaxValueValidator,
    MinLengthValidator,
    MinValueValidator,
    PropertyValidator,
} from './settings-devices-configuration-layout.model.ts';
import {
    ValidationFunction,
    ValidationMultiValueFunction,
    ValidationResult,
    validations,
} from './settings-devices-configuration.validation.ts';

const defaultValidation: PocInputValidity = {
    state: 'valid',
    type: [],
};

type ComponentMapType = Record<DeviceLayoutConfigRepresentationType, () => ReactElement>;

interface PropertyComponentProps {
    property: DeviceLayoutConfigSectionProperty;
}

const getValidationMessages = (
    deviceConfigurationValidation: DeviceConfigurationValidity,
    propertyName: string,
    t: TFunction
): ExtendedValidationMessages => ({
    error: {
        greater_than: t('settings.devices.device-settings.device-config-layout.erroneous-combinations.less-than', {
            propertyName: deviceConfigurationValidation?.[propertyName]?.erroneousCombinations?.[0]?.targetProperty,
        }),
        less_than: t('settings.devices.device-settings.device-config-layout.erroneous-combinations.greater-than', {
            propertyName: deviceConfigurationValidation?.[propertyName]?.erroneousCombinations?.[0]?.targetProperty,
        }),
        equals: t('settings.devices.device-settings.device-config-layout.erroneous-combinations.equals', {
            propertyName: deviceConfigurationValidation?.[propertyName]?.erroneousCombinations?.[0]?.targetProperty,
        }),
    },
});

type PropertyRenderValue = string | string[] | undefined;

const hasContent = (raw: unknown): raw is string | string[] =>
    (typeof raw === 'string' || Array.isArray(raw)) && raw.length > 0;

// Not narrowed: DatePicker writes back a Date (DatepickerValueType), which must survive the round trip.
const asRenderValue = (raw: unknown): PropertyRenderValue => raw as PropertyRenderValue;

// The datepicker's props exclude the ISO string the configuration API stores, which is what it gets today.
const asDatepickerValue = (value: PropertyRenderValue): PocDatepickerValueType => value as PocDatepickerValueType;

const toValidationValue = (value: unknown): string | string[] => {
    if (Array.isArray(value)) {
        return value;
    }
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
};

export const PropertyComponent: React.FC<PropertyComponentProps> = ({ property }) => {
    const { t } = useTranslation();

    const { deviceConfigurationInstance, setDeviceConfigurationProperty, isPropertyDisabled, getPropertyValue } =
        useDeviceConfiguration();

    const { setValidation, deviceConfigurationValidation, checkErroneousCombinations } = useConfigurationValidation();

    const updatedProperties = deviceConfigurationInstance?.properties || {};
    const validationMessages = getValidationMessages(deviceConfigurationValidation, property.name, t);

    const handleChange = useCallback(
        (field: string, value: unknown) => {
            setTimeout(() => {
                checkErroneousCombinations();
                handleValidation(toValidationValue(value));
            }, 0);
            setDeviceConfigurationProperty(field, value);
        },
        [setDeviceConfigurationProperty, checkErroneousCombinations]
    );

    const handleValidation = useCallback(
        (value: string | string[]) => {
            if (!property?.validations || property.validations.length === 0) {
                return;
            }

            const validation = property.validations.reduce<PocInputValidity>(
                (acc, validator) => {
                    const { error, isValid } = getValidationFunction(value, validator);
                    return isValid ? acc : upsertErrorType(acc, error);
                },
                { ...defaultValidation }
            );

            setValidation(property.name, validation);
        },
        [property, setValidation]
    );

    const upsertErrorType = (validation: PocInputValidity, type: PocInputWarning | PocInputError): PocInputValidity => {
        const currentErrors = isArray(validation.type) ? validation.type : [];
        return {
            state: 'error',
            type: currentErrors.includes(type) ? currentErrors : [...currentErrors, type],
        };
    };

    const getValidationFunction = (value: string | string[], validator: PropertyValidator): ValidationResult =>
        isArray(value)
            ? (validations[validator.type] as unknown as ValidationMultiValueFunction)(value, validator)
            : (validations[validator.type] as unknown as ValidationFunction)(value, validator);

    const getMaxSelectable = useCallback(() => {
        const maxSelectable = property.validations?.find(
            (prop) => prop.type === 'MAX_SELECTABLE_VALUES'
        ) as MaxSelectableValuesValidator;
        return maxSelectable?.maximum;
    }, [property.validations]);

    const currentValue = getPropertyValue(property.name);
    let value: PropertyRenderValue;
    if (hasContent(currentValue)) {
        value = currentValue;
    } else if (updatedProperties[property.name] == null) {
        value = property.defaultValue;
    } else {
        value = asRenderValue(updatedProperties[property.name]);
    }
    const textValue = typeof value === 'string' ? value : '';

    const disabledProperty = isPropertyDisabled(property.name) ?? property.status === PROPERTY_STATUS.DISABLED;

    const maxLength = (property.validations?.find((v) => v.type === 'MAX_LENGTH') as MaxLengthValidator)?.length;
    const minLength = (property.validations?.find((v) => v.type === 'MIN_LENGTH') as MinLengthValidator)?.length;
    const maxValue = (property.validations?.find((v) => v.type === 'MAX_VALUE') as MaxValueValidator)?.value;
    const minValue = (property.validations?.find((v) => v.type === 'MIN_VALUE') as MinValueValidator)?.value;

    const inputTypeMap: Record<DeviceLayoutConfigPropertyContentType, PocInputType> = {
        STRING: 'text',
        TIME: 'time',
        DATE: 'date',
        NUMBER: 'number',
        BOOLEAN: 'text',
        MULTI_OPTION: 'text',
    };

    const resolvedInputType = inputTypeMap[property.contentType] ?? 'number';

    const componentMap: ComponentMapType = {
        TEXTBOX: () => (
            <Input
                name={property.name}
                label={property.status === PROPERTY_STATUS.DISABLED ? '' : property.displayName}
                placeholder={''}
                validity={deviceConfigurationValidation?.[property.name]?.validity ?? defaultValidation}
                type={resolvedInputType}
                onChange={handleChange}
                value={textValue}
                spaceForError
                showCharCount
                maxlength={maxLength}
                minlength={minLength}
                maxValue={maxValue}
                minValue={minValue}
                required={property.mandatory}
                disabled={disabledProperty}
                customValidationCode={
                    (deviceConfigurationValidation?.[property.name]?.erroneousCombinations?.[0]
                        ?.code as CustomValidationCode) ?? undefined
                }
                validationMessages={validationMessages}
            />
        ),
        TEXTAREA: () => (
            <Input
                name={property.name}
                label={property.displayName}
                validity={deviceConfigurationValidation?.[property.name]?.validity ?? defaultValidation}
                placeholder={''}
                type={resolvedInputType}
                onChange={handleChange}
                value={textValue}
                showCharCount
                spaceForError
                maxlength={maxLength}
                minlength={minLength}
                maxValue={maxValue}
                minValue={minValue}
                required={property.mandatory}
                disabled={disabledProperty}
            />
        ),
        PASSWORD_TEXTBOX: () => (
            <Input
                name={property.name}
                label={property.displayName}
                validity={deviceConfigurationValidation?.[property.name]?.validity ?? defaultValidation}
                placeholder={''}
                type={'password'}
                onChange={handleChange}
                value={textValue}
                spaceForError
                showCharCount
                maxlength={maxLength}
                minlength={minLength}
                maxValue={maxValue}
                minValue={minValue}
                required={property.mandatory}
                disabled={disabledProperty}
            />
        ),
        LIST: () => (
            <Select
                name={property.name}
                label={property.displayName}
                value={value ?? []}
                validity={deviceConfigurationValidation?.[property.name]?.validity ?? defaultValidation}
                required={property.mandatory}
                disabled={disabledProperty}
                spaceForError
                options={
                    property.options?.map(
                        (option) =>
                            ({
                                value: option.id,
                                text: option.description,
                            }) as SelectOption
                    ) ?? []
                }
                onChange={handleChange}
            />
        ),
        RADIO_GROUP: () => (
            <div>
                <PocTypography variant="subtitle1" className="header-title">
                    {property.displayName}
                </PocTypography>
                <PocRadioGroup
                    name={property.name}
                    value={textValue}
                    direction="vertical"
                    onValueChange={(e) => handleChange(property.name, e.detail)}
                    disabled={disabledProperty}
                >
                    {property.options?.map((option) => (
                        <PocRadio
                            key={option.id}
                            data-testid={`radio-option-${option.id}`}
                            value={option.id}
                            disabled={disabledProperty}
                        >
                            {option.description}
                        </PocRadio>
                    ))}
                </PocRadioGroup>
            </div>
        ),
        CHECKBOX_GROUP: () => (
            <CheckboxGroupComponent
                disabled={disabledProperty}
                value={Array.isArray(value) ? value : [value]}
                maxSelectable={getMaxSelectable()}
                property={property}
                onValueChange={(newValue) => handleChange(property.name, newValue)}
            />
        ),
        DATEPICKER: () => (
            <DatePicker
                name={property.name}
                label={property.displayName}
                value={asDatepickerValue(value)}
                onChange={handleChange}
                disabled={disabledProperty}
                required={property.mandatory}
            />
        ),
    };

    const ComponentRenderer = componentMap[property.representationType];

    return ComponentRenderer ? ComponentRenderer() : <>{property.displayName}</>;
};
